/**
 * Item / block icons for every container, the hotbar and the cursor stack.
 * Extracted from the old HUD (makeIcon / drawTile / drawCubeIcon / drawModelIcon /
 * applyTint), which stays untouched in hud.ts.
 *
 * `get(id)` is a cached 32x32 canvas (blocks: isometric cube sprite; items: the
 * vanilla sprite via drawItemIcon). `decorate` paints a slot's icon, count and
 * durability bar in GUI px (the slot element's origin is the item's top-left;
 * the 16x16 item area, as in vanilla's slot coordinates).
 */

import { durabilityBar } from './heartStyle';
import { ATLAS_COLS, CELL, GUTTER, TILE } from '../atlasLayout';
import { MASK_ALPHA } from '../render/atlasOverlays';
import { iconTint, MASKED_TINT_TILES } from '../biomeTint';
import { AIR, BED_FOOT_ID, BED_HEAD_ID, BLOCKS, DOOR_BOTTOM_TO_TOP, RenderShape } from '../blocks';
import { modelQuads, type Quad } from '../models';
import { drawItemIcon, isItem, potionIconFor } from '../items';
import { isPotionItem } from '../game/brewing/potions';
import type { AtlasResult } from '../textures';
import { enchKey, isEmpty, type Stack } from '../game/types';
import { I } from '../game/itemIds';
import { maxDamageOf } from '../game/itemdb';
import { mcText } from './kit';

/** Face brightness by mesher face id (+X,-X,+Y,-Y,+Z,-Z, oblique) — the chunk
 *  shader's FACE_SHADE (render/materials.ts). */
const FACE_SHADE = [0.68, 0.68, 1.0, 0.55, 0.84, 0.84, 1.0];

/** The item icon geometry of a block-entity model: the chest facing +Z (latch
 *  on the left, as in vanilla's GUI), the bed as both halves, head toward +X. */
function iconQuads(id: number): Quad[] | null {
  const none = { px: AIR, nx: AIR, pz: AIR, nz: AIR };
  if (id === BED_FOOT_ID || id === BED_HEAD_ID) {
    const foot = modelQuads(BED_FOOT_ID, { ...none, px: BED_HEAD_ID });
    const head = modelQuads(BED_HEAD_ID, { ...none, nx: BED_FOOT_ID });
    return [...foot, ...head.map((q) => ({ ...q, pos: q.pos.map((v, i) => (i % 3 === 0 ? v + 1 : v)) }))];
  }
  if (BLOCKS[id].shape !== RenderShape.Model || DOOR_BOTTOM_TO_TOP[id] !== undefined) return null;
  return [...modelQuads(id, none)];
}

const ICON_FACES: ReadonlyArray<
  readonly [face: number, a: number, b: number, c: number, d: number, e: number, f: number, shade: number]
> = [
  [2, 1, -0.5, 1, 0.5, 0, 8, 1.0],   // +Y — the top rhombus
  [4, 1, 0.5, 0, 1, 0, 8, 0.84],     // +Z — the front face, down the left
  [0, 1, -0.5, 0, 1, 16, 16, 0.68],  // +X — down the right
];

export class ItemIcons {
  private readonly cache = new Map<number, HTMLCanvasElement>();
  private readonly iconScratch: HTMLCanvasElement;

  constructor(private readonly atlas: AtlasResult) {
    this.iconScratch = document.createElement('canvas');
    this.iconScratch.width = this.iconScratch.height = 32;
    ItemIcons.shared = this;
  }

  /** Cached 32x32 icon; do not mutate. */
  get(id: number): HTMLCanvasElement {
    let c = this.cache.get(id);
    if (!c) {
      c = document.createElement('canvas');
      c.width = c.height = 32;
      const ctx = c.getContext('2d')!;
      ctx.imageSmoothingEnabled = false;
      this.drawTile(ctx, id);
      this.cache.set(id, c);
    }
    return c;
  }

  /** Icon for a concrete stack: potions are tinted by their type (Stack.dmg), everything else is `get(id)`. */
  iconFor(stack: Stack): HTMLCanvasElement {
    if (!isPotionItem(stack.id)) return this.get(stack.id);
    const key = stack.id * 1024 + stack.dmg;
    let c = this.potionCache.get(key);
    if (!c) {
      c = document.createElement('canvas');
      c.width = c.height = 32;
      const ctx = c.getContext('2d')!;
      ctx.imageSmoothingEnabled = false;
      const src = potionIconFor(stack.id, stack.dmg);
      if (!src) return this.get(stack.id);
      ctx.drawImage(src, 0, 0, 32, 32);
      this.potionCache.set(key, c);
    }
    return c;
  }
  private readonly potionCache = new Map<number, HTMLCanvasElement>();

  /** Drop cached icons (e.g. after the item sprites finished loading late). */
  invalidate(): void { this.cache.clear(); this.potionCache.clear(); }

  draw(ctx: CanvasRenderingContext2D, id: number, dx: number, dy: number, size: number): void {
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(this.get(id), dx, dy, size, size);
  }

  /**
   * Paint `stack` into a slot element (origin = the 16x16 item area, GUI px):
   * icon, count (vanilla: right-aligned to x+17, y+9, white with shadow) and the
   * durability bar (13x2 at +2,+13, green->red). Cheap to call every frame: a
   * key on the element skips unchanged stacks.
   */
  static decorate(slotEl: HTMLElement, stack: Stack): void {
    const key = isEmpty(stack) ? '' : `${stack.id}:${stack.count}:${stack.dmg}:${stack.ench?.length ? enchKey(stack) : ''}`;
    if (slotEl.dataset.k === key) return;
    slotEl.dataset.k = key;
    slotEl.replaceChildren();
    if (key === '') return;
    const icons = ItemIcons.shared;
    if (icons) {
      const c = document.createElement('canvas');
      c.width = c.height = 32;
      c.style.cssText = 'position:absolute;left:0;top:0;width:16px;height:16px;image-rendering:pixelated;pointer-events:none;';
      c.getContext('2d')!.drawImage(icons.iconFor(stack), 0, 0);
      slotEl.appendChild(c);
      if (stack.ench?.length || stack.id === I.ENCHANTED_BOOK || stack.id === I.ENCHANTED_GOLDEN_APPLE) slotEl.appendChild(glintFor(icons.get(stack.id), stack.id));
    }
    const max = maxDamageOf(stack.id);
    const dur = durabilityBar(stack.dmg, max);
    if (dur) {
      const bar = document.createElement('div');
      bar.style.cssText = 'position:absolute;left:2px;top:13px;width:13px;height:2px;background:#000;pointer-events:none;';
      const fill = document.createElement('div');
      fill.style.cssText = `position:absolute;left:0;top:0;width:${dur.width}px;height:1px;background:${dur.color};`;
      bar.appendChild(fill);
      slotEl.appendChild(bar);
    }
    if (stack.count > 1) {
      const t = mcText(String(stack.count), { color: '#ffffff', shadow: true });
      t.style.cssText = `position:absolute;left:${17 - (t.width - 1)}px;top:9px;width:${t.width}px;height:${t.height}px;image-rendering:pixelated;pointer-events:none;`;
      slotEl.appendChild(t);
    }
  }

  /** Instance used by the static `decorate` (set by the first constructed ItemIcons). */
  static shared: ItemIcons | null = null;

  private drawTile(ctx: CanvasRenderingContext2D, id: number): void {
    if (isItem(id)) {
      drawItemIcon(ctx, id, 32);
      return;
    }
    // A door's own tile is just its bottom panel, so drawing only that shows
    // half a door. Vanilla's door item sprite is the two halves stacked into
    // one narrow, full-height door — do the same here.
    const topHalf = DOOR_BOTTOM_TO_TOP[id];
    if (topHalf !== undefined) {
      this.blitTile(ctx, topHalf, 8, 0, 16, 16);
      this.blitTile(ctx, id, 8, 16, 16, 16);
      return;
    }
    const quads = iconQuads(id);
    if (quads) {
      this.drawModelIcon(ctx, quads);
      return;
    }
    // A full cube gets the isometric sprite vanilla renders for block items.
    // A flat blit of the top face — what this used to do — never shows the
    // sides, so a furnace's door, a log's bark or a chest's front are invisible
    // in the hotbar.
    if (BLOCKS[id].shape === RenderShape.Cube) {
      this.drawCubeIcon(ctx, id);
      return;
    }
    const tile = this.tileOf(id);
    this.blitTile(ctx, id, 0, 0, 32, 32);
    // Grayscale tiles (grass top, leaves, plants) only get their colour from a
    // tint; the mesher bakes one into the world mesh's vertex colours, so the
    // icon has to apply the same one here or it renders grey.
    const tint = iconTint(id, tile);
    if (tint !== null) this.applyTint(ctx, tint, MASKED_TINT_TILES[tile] === 1);
  }
  /**
   * Isometric block sprite for a full cube: the top face plus the two side
   * faces a 3D item render would show, on a 2:1 dimetric projection.
   *
   * The right-hand face is the block's +X face and the left-hand one its +Z
   * face, which is the face every directional block puts its front on — so a
   * furnace's door and the chest's latch land on the left, as in vanilla. Each
   * face is shaded with the chunk shader's own face brightness (materials.ts
   * FACE_SHADE) so an icon is lit like the block it stands for.
   */
  private drawCubeIcon(ctx: CanvasRenderingContext2D, id: number): void {
    const tiles = BLOCKS[id].tiles;
    const sctx = this.iconScratch.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;
    sctx.imageSmoothingEnabled = true; // the tile is drawn at half scale
    for (const [face, a, b, c, d, e, f, shade] of ICON_FACES) {
      const tile = tiles[face] ?? tiles[0];
      const col = tile % ATLAS_COLS;
      const row = (tile / ATLAS_COLS) | 0;
      sctx.setTransform(1, 0, 0, 1, 0, 0);
      sctx.clearRect(0, 0, 32, 32);
      // map the tile onto the face's parallelogram: the 16x16 texture box at
      // (e,f) with the face's two edge vectors as its axes
      sctx.setTransform(a, b, c, d, e, f);
      sctx.drawImage(this.atlas.canvas, col * CELL + GUTTER, row * CELL + GUTTER, TILE, TILE, 0, 0, 16, 16);
      sctx.setTransform(1, 0, 0, 1, 0, 0);
      const tint = iconTint(id, tile);
      if (tint !== null) this.applyTint(sctx, tint, MASKED_TINT_TILES[tile] === 1);
      if (shade < 1) {
        sctx.globalCompositeOperation = 'source-atop';
        sctx.fillStyle = `rgba(0,0,0,${1 - shade})`;
        sctx.fillRect(0, 0, 32, 32);
        sctx.globalCompositeOperation = 'source-over';
      }
      // the faces only share their edges, so drawing one over the next never
      // eats into it — and the sprite grows corner-to-corner like the real cube
      ctx.drawImage(this.iconScratch, 0, 0);
    }
  }
  /**
   * Isometric sprite of a baked model (chest, bed), on the same projection as
   * drawCubeIcon — a unit cube lands exactly where a cube icon's faces do — and
   * scaled down to fit when the model is bigger than a block. Quads are culled
   * by screen winding, drawn back to front, each through the affine map from
   * its tile onto its on-screen parallelogram and shaded like its face.
   */
  private drawModelIcon(ctx: CanvasRenderingContext2D, quads: readonly Quad[]): void {
    // block space -> icon px: x runs right-down, z left-down, y up
    const proj = (x: number, y: number, z: number): [number, number] =>
      [16 * x - 16 * z + 16, 8 * x + 8 * z + 16 * (1 - y)];
    const items = quads.map((q) => {
      const pts = [0, 1, 2, 3].map((k) => proj(q.pos[k * 3], q.pos[k * 3 + 1], q.pos[k * 3 + 2]));
      let area = 0;
      for (let k = 0; k < 4; k++) {
        const a = pts[k], b = pts[(k + 1) & 3];
        area += a[0] * b[1] - b[0] * a[1];
      }
      let depth = 0;
      for (let k = 0; k < 12; k++) depth += q.pos[k];
      return { q, pts, area, depth };
    }).filter((it) => it.area < -1e-6).sort((a, b) => a.depth - b.depth);
    if (items.length === 0) return;
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const it of items) {
      for (const [px, py] of it.pts) {
        x0 = Math.min(x0, px); y0 = Math.min(y0, py); x1 = Math.max(x1, px); y1 = Math.max(y1, py);
      }
    }
    const k = Math.min(1, 32 / (x1 - x0), 32 / (y1 - y0));
    const ox = 16 - ((x0 + x1) / 2) * k, oy = 16 - ((y0 + y1) / 2) * k;

    const sctx = this.iconScratch.getContext('2d')!;
    for (const { q, pts } of items) {
      const P = pts.map(([px, py]) => [px * k + ox, py * k + oy]);
      const U = [0, 1, 2, 3].map((i) => [q.uv[i * 2] * TILE, q.uv[i * 2 + 1] * TILE]);
      // affine (tile px -> icon px) from corners 0, 1, 3
      const a1 = U[1][0] - U[0][0], b1 = U[1][1] - U[0][1];
      const a3 = U[3][0] - U[0][0], b3 = U[3][1] - U[0][1];
      const det = a1 * b3 - a3 * b1;
      if (Math.abs(det) < 1e-9) continue;
      const p1x = P[1][0] - P[0][0], p1y = P[1][1] - P[0][1];
      const p3x = P[3][0] - P[0][0], p3y = P[3][1] - P[0][1];
      const ma = (p1x * b3 - p3x * b1) / det, mc = (p3x * a1 - p1x * a3) / det;
      const mb = (p1y * b3 - p3y * b1) / det, md = (p3y * a1 - p1y * a3) / det;
      const me = P[0][0] - ma * U[0][0] - mc * U[0][1];
      const mf = P[0][1] - mb * U[0][0] - md * U[0][1];

      sctx.setTransform(1, 0, 0, 1, 0, 0);
      sctx.clearRect(0, 0, 32, 32);
      sctx.save();
      sctx.beginPath();
      sctx.moveTo(P[0][0], P[0][1]);
      for (let i = 1; i < 4; i++) sctx.lineTo(P[i][0], P[i][1]);
      sctx.closePath();
      sctx.clip();
      sctx.imageSmoothingEnabled = false;
      sctx.setTransform(ma, mb, mc, md, me, mf);
      const col = q.tile % ATLAS_COLS, row = (q.tile / ATLAS_COLS) | 0;
      sctx.drawImage(this.atlas.canvas, col * CELL + GUTTER, row * CELL + GUTTER, TILE, TILE, 0, 0, TILE, TILE);
      sctx.restore();
      const shade = FACE_SHADE[q.face];
      if (shade < 1) {
        sctx.globalCompositeOperation = 'source-atop';
        sctx.fillStyle = `rgba(0,0,0,${1 - shade})`;
        sctx.fillRect(0, 0, 32, 32);
        sctx.globalCompositeOperation = 'source-over';
      }
      ctx.drawImage(this.iconScratch, 0, 0);
    }
  }
  /** The single tile a non-cube icon shows: the block's top face where it has
   *  one, else its side (plants, doors, ground cover). Cubes are drawn as
   *  sprites instead — see drawCubeIcon. */
  private tileOf(id: number): number {
    const def = BLOCKS[id];
    return def.tiles[2] ?? def.tiles[0];
  }

  /** Copy a block's icon tile out of the atlas into a rect on `ctx`. */
  private blitTile(
    ctx: CanvasRenderingContext2D, id: number, dx: number, dy: number, dw: number, dh: number,
  ): void {
    const tile = this.tileOf(id);
    const col = tile % ATLAS_COLS;
    const row = Math.floor(tile / ATLAS_COLS);
    ctx.drawImage(this.atlas.canvas, col * CELL + GUTTER, row * CELL + GUTTER, TILE, TILE, dx, dy, dw, dh);
  }

  /**
   * Multiply an icon's pixels by `tint` (0xRRGGBB). Masked tiles (a grass
   * block's side) only tint the pixels the atlas marked with MASK_ALPHA, and
   * those are restored to fully opaque — the marker is a shader-side flag,
   * not something a 2D icon should show as transparency.
   */
  private applyTint(ctx: CanvasRenderingContext2D, tint: number, masked: boolean): void {
    const img = ctx.getImageData(0, 0, 32, 32);
    const d = img.data;
    const tr = (tint >> 16) & 0xff, tg = (tint >> 8) & 0xff, tb = tint & 0xff;
    for (let i = 0; i < d.length; i += 4) {
      const a = d[i + 3];
      if (a === 0) continue;
      if (masked) {
        if (a !== MASK_ALPHA) continue;
        d[i + 3] = 255;
      }
      d[i] = d[i] * tr / 255;
      d[i + 1] = d[i + 1] * tg / 255;
      d[i + 2] = d[i + 2] * tb / 255;
    }
    ctx.putImageData(img, 0, 0);
  }
}

// --- enchantment glint -------------------------------------------------------------
// Vanilla (RenderType.glint): the item is drawn again with misc/enchanted_glint_item.png,
// UVs scaled 8x, rotated 10 degrees and scrolled (x every 110 s/4, y every 30 s/4 at glint
// speed 0.5), blended additively (SRC_COLOR, ONE) at the 0.75 default strength. Here: a
// 16x16 layer masked by the icon's alpha with `plus-lighter` blending; the texture is
// shown at 128 px per 16 px icon (= the 8x UV scale over a 1/8th-size atlas cell).
let glintCss = false;
function glintFor(icon: HTMLCanvasElement, _id: number): HTMLDivElement {
  if (!glintCss) {
    glintCss = true;
    const st = document.createElement('style');
    st.textContent =
      '@keyframes ocGlintX{from{background-position-x:0}to{background-position-x:-128px}}' +
      '@keyframes ocGlintY{from{background-position-y:0}to{background-position-y:128px}}';
    document.head.appendChild(st);
  }
  const mask = `url(${icon.toDataURL()})`;
  const d = document.createElement('div');
  d.style.cssText =
    'position:absolute;left:0;top:0;width:16px;height:16px;overflow:hidden;pointer-events:none;' +
    'mix-blend-mode:plus-lighter;opacity:0.75;' +
    `-webkit-mask-image:${mask};mask-image:${mask};-webkit-mask-size:16px 16px;mask-size:16px 16px;`;
  const inner = document.createElement('div');
  inner.style.cssText =
    'position:absolute;left:-40px;top:-40px;width:96px;height:96px;transform:rotate(10deg);' +
    'background:url(mc/misc/enchanted_glint_item.png) repeat;background-size:128px 128px;image-rendering:pixelated;' +
    // vanilla glint at speed 0.5: x period 27.5 s, y period 7.5 s
    'animation:ocGlintX 27.5s linear infinite,ocGlintY 7.5s linear infinite;';
  d.appendChild(inner);
  return d;
}
